import {ImageOpContextHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-hal"
import {MipLevelRegion, Regions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {CreateDataArgs, ImageOpHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal"
import {Box2, Box2Like, ColorLike, Matrix3x2, Size2Like, Vector2, wrap} from "@cm/math"
import {HalGeometryRef, HalImage} from "@cm/hal"
import {deepCopy, deepEqual} from "@cm/utils"
import {colorFromRemotelyColorLike} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-utils"
import {BrushStroke, definition} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/brush-strokes/definition"
import {HalPainterPrimitiveRef} from "@cm/hal/hal-ref"
import {RegionLockedHalImageRef} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/region-locked-hal-image-ref"
import {ImageOpHalParameters, UpdateImageOptions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-types"
import {AnyImageOpHalData, CtorArgs, ImageOpHalData} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-data"
import {UPDATE_COMPLEXITY_MID} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/update-complexity"

const BRUSH_SPLAT_SPACING = 0.02 // portion of brush width

type Definition = typeof definition

export class ImageOpBrushStrokeHal extends ImageOpHal<Definition, ImageOpBrushStrokeHalData> {
    constructor(ctx: ImageOpContextHal, parameters: ImageOpHalParameters<Definition>) {
        super(ctx)
        this.setParameters(parameters)
    }

    protected override createData(args: CreateDataArgs<Definition, ImageOpBrushStrokeHalData>): ImageOpBrushStrokeHalData {
        return new ImageOpBrushStrokeHalData({
            ...args,
            descriptor: args.parameters.sourceImage.descriptor,
            outputImageReuse: "reuse-if-same-descriptor",
            updateComplexity: UPDATE_COMPLEXITY_MID,
            invalidateEntireOutputOnParameterChange: false,
        })
    }
}

class ImageOpBrushStrokeHalData extends ImageOpHalData<Definition> {
    constructor(args: CtorArgs<Definition, ImageOpBrushStrokeHalData>) {
        super(args)
        this.drawStrokePainter =
            args.prevData?.drawStrokePainter.addRef() ??
            this.ctx.createPainter(
                "primitive",
                `
            float smootherstep(float edge0, float edge1, float x) {
                x = clamp((x - edge0) / (edge1 - edge0), 0.0, 1.0);            
                return x * x * x * (x * (6.0f * x - 15.0f) + 10.0f);
            }

            vec4 computeColor(vec2 worldPosition, vec2 uv, vec4 color) {
                uv = uv * 2.0 - 1.0;
                float r = length(uv);
                float hardness = color.a;
                // Adaptive clamp: ensure the AA falloff band is always ~1 pixel wide
                // regardless of brush size. aaRadius ≈ 2/width (r-space size of one pixel).
                float aaRadius = length(vec2(dFdx(r), dFdy(r)));
                float v = 1.0 - smootherstep(min(max(0.0, 1.0 - aaRadius), hardness), 1.0, min(1.0, r));
                return vec4(color.rgb * v, v);
            }
        `,
            )
        // if we kept the same output-image, keep the geometry as well so we continue to write incrementally
        if (args.prevData?.outputImagePtr === this.outputImagePtr) {
            this.drawStrokeGeometry = args.prevData.drawStrokeGeometry.addRef()
            this.strokeGeometryInfos = deepCopy(args.prevData.strokeGeometryInfos)
            this.boundingBox = deepCopy(args.prevData.boundingBox)
        } else {
            this.drawStrokeGeometry = this.ctx.createGeometry()
            this.strokeGeometryInfos = []
            this.boundingBox = new Box2()
        }
        this.updateGeometry(args.parameters.brushStrokes)
    }

    override dispose() {
        this.drawStrokePainter.release()
        this.drawStrokeGeometry.release()
        super.dispose()
    }

    getBoundingBox(): Box2Like {
        return this.boundingBox
    }

    // ImageHal
    protected override async writeRegion(outputImage: HalImage, mipLevelRegion: MipLevelRegion, options?: UpdateImageOptions): Promise<void> {
        const {sourceImage} = this.parameters
        sourceImage.addRef("BrushStrokes.writeRegion - sourceImage")
        let sourceHalImage: RegionLockedHalImageRef | undefined
        try {
            sourceHalImage = await sourceImage.getUpdatedHalImage(mipLevelRegion, options)
            // copy the source image to the local image
            await this.ctx.copyImageData({
                targetImage: outputImage,
                sourceImage: sourceHalImage.halImage,
                region: mipLevelRegion,
                mipLevel: mipLevelRegion.mipLevel,
            })
            // draw the brush strokes (we draw all strokes at once; technically this could cause artifact as drawing order matters for strokes of different colors, but in practice this
            // has not been observed and make drawing much faster)
            const mipLevelScale = 1 / (1 << mipLevelRegion.mipLevel)
            this.drawStrokePainter.ref.paint({
                target: outputImage,
                geometry: this.drawStrokeGeometry.ref,
                options: {
                    blendMode: "over",
                    targetMipLevel: mipLevelRegion.mipLevel,
                    clipRegion: mipLevelRegion,
                    transform: new Matrix3x2().scale(new Vector2(mipLevelScale, mipLevelScale)),
                },
            })
        } finally {
            sourceHalImage?.release()
            sourceImage.release("BrushStrokes.writeRegion - sourceImage")
        }
    }

    protected override onSourceRegionsInvalidated(_sourceImage: AnyImageOpHalData, sourceRegions: Regions) {
        this.invalidateRegions(sourceRegions)
    }

    private updateGeometry(brushStrokes: BrushStroke[]) {
        const geometry = this.drawStrokeGeometry

        const regionsToInvalidate: Regions = []
        this.boundingBox.makeEmpty()
        let firstMismatchingStrokeIndex = 0
        for (; firstMismatchingStrokeIndex < brushStrokes.length; firstMismatchingStrokeIndex++) {
            if (firstMismatchingStrokeIndex >= this.strokeGeometryInfos.length) {
                break
            }
            const brushStroke = brushStrokes[firstMismatchingStrokeIndex]
            const strokeGeometryInfo = this.strokeGeometryInfos[firstMismatchingStrokeIndex]
            if (!deepEqual(strokeGeometryInfo.brushStroke, brushStroke)) {
                break
            }
            this.boundingBox.expandByBoxInPlace(strokeGeometryInfo.boundingBox)
        }
        // remove all strokes that don't match the new strokes
        if (firstMismatchingStrokeIndex < this.strokeGeometryInfos.length) {
            {
                // remove the geometry
                const strokeGeometryInfo = this.strokeGeometryInfos[firstMismatchingStrokeIndex]
                geometry.ref.removeVertices(strokeGeometryInfo.baseVertex, geometry.ref.numVertices - strokeGeometryInfo.baseVertex)
                geometry.ref.removeIndices(strokeGeometryInfo.baseIndex, geometry.ref.numIndices - strokeGeometryInfo.baseIndex)
            }
            // invalidate regions
            for (let i = firstMismatchingStrokeIndex; i < this.strokeGeometryInfos.length; i++) {
                const strokeGeometryInfo = this.strokeGeometryInfos[i]
                regionsToInvalidate.push(strokeGeometryInfo.boundingBox)
            }
            this.strokeGeometryInfos.length = firstMismatchingStrokeIndex
        }

        // add new strokes
        for (let i = firstMismatchingStrokeIndex; i < brushStrokes.length; i++) {
            const brushStroke = brushStrokes[i]
            const strokeGeometryInfo = this.addStrokeGeometrySplats(geometry, brushStroke, this.parameters.sourceImage.descriptor)
            this.strokeGeometryInfos.push(strokeGeometryInfo)
            regionsToInvalidate.push(strokeGeometryInfo.boundingBox)
            this.boundingBox.expandByBoxInPlace(strokeGeometryInfo.boundingBox)
        }
        this.boundingBox.expandToIntegersInPlace()
        this.invalidateRegions(regionsToInvalidate)
    }

    private addStrokeGeometrySplats(geometry: HalGeometryRef, brushStroke: BrushStroke, targetSize: Size2Like): StrokeGeometryInfo {
        if (brushStroke.settings.width <= 0) {
            throw Error("Thickness must be positive")
        }
        const brushColor = colorFromRemotelyColorLike(brushStroke.settings.color)
        const geometryColor: ColorLike = {
            r: brushColor.r,
            g: brushColor.g,
            b: brushColor.b,
            a: brushStroke.settings.hardness,
        }
        const baseVertex = geometry.ref.numVertices
        const baseIndex = geometry.ref.numIndices
        const boundingBox = new Box2()
        const splatDistance = brushStroke.settings.width * BRUSH_SPLAT_SPACING
        if (brushStroke.path.length >= 1) {
            boundingBox.expandByBoxInPlace(
                this.addTileableQuad(geometry, Vector2.fromVector2Like(brushStroke.path[0]), brushStroke.settings.width, geometryColor, targetSize),
            )
        }
        if (brushStroke.path.length >= 2) {
            let splatDistanceRemainder = 0
            for (let i = 1; i < brushStroke.path.length; i++) {
                const p0 = Vector2.fromVector2Like(brushStroke.path[i - 1])
                const p1 = Vector2.fromVector2Like(brushStroke.path[i])
                const delta = p1.sub(p0)
                const distance = delta.norm()
                const dir = delta.mul(1 / distance)
                while (splatDistanceRemainder < distance) {
                    const pos = p0.add(dir.mul(splatDistanceRemainder))
                    const quadBoundingBox = this.addTileableQuad(geometry, pos, brushStroke.settings.width, geometryColor, targetSize)
                    boundingBox.expandByBoxInPlace(quadBoundingBox)
                    splatDistanceRemainder += splatDistance
                }
                splatDistanceRemainder -= distance
            }
        }
        return {
            baseVertex,
            baseIndex,
            numVertices: geometry.ref.numVertices - baseVertex,
            numIndices: geometry.ref.numIndices - baseIndex,
            boundingBox,
            brushStroke: deepCopy(brushStroke),
        }
    }

    private addTileableQuad(geometry: HalGeometryRef, pos: Vector2, width: number, color: ColorLike, targetSize: Size2Like): Box2 {
        if (this.parameters.options.tilingEnabled) {
            pos = new Vector2(wrap(pos.x, targetSize.width), wrap(pos.y, targetSize.height))
        }
        const boundingBox = new Box2()
        boundingBox.expandByBoxInPlace(this.addQuad(geometry, pos, width, color))
        if (this.parameters.options.tilingEnabled) {
            const repeatX = pos.x > targetSize.width - width / 2 || pos.x < width / 2
            const repeatY = pos.y > targetSize.height - width / 2 || pos.y < width / 2
            const signX = pos.x > targetSize.width / 2 ? -1 : 1
            const signY = pos.y > targetSize.height / 2 ? -1 : 1
            if (repeatX && repeatY) {
                boundingBox.expandByBoxInPlace(this.addQuad(geometry, pos.add({x: targetSize.width * signX, y: targetSize.height * signY}), width, color))
            }
            if (repeatX) {
                boundingBox.expandByBoxInPlace(this.addQuad(geometry, pos.add({x: targetSize.width * signX, y: 0}), width, color))
            }
            if (repeatY) {
                boundingBox.expandByBoxInPlace(this.addQuad(geometry, pos.add({x: 0, y: targetSize.height * signY}), width, color))
            }
        }
        boundingBox.expandToIntegersInPlace()
        boundingBox.intersectInPlace(new Box2(0, 0, targetSize.width, targetSize.height))
        return boundingBox
    }

    private addQuad(geometry: HalGeometryRef, pos: Vector2, width: number, color: ColorLike): Box2 {
        // +1 for adding 1 pixel which is not drawn by the hw rasterizer
        const p00 = new Vector2(pos.x - width / 2, pos.y - width / 2)
        const p10 = new Vector2(pos.x + width / 2 + 1, pos.y - width / 2)
        const p01 = new Vector2(pos.x - width / 2, pos.y + width / 2 + 1)
        const p11 = new Vector2(pos.x + width / 2 + 1, pos.y + width / 2 + 1)
        const baseIndex = geometry.ref.addVertices([p00, p10, p01, p11], [new Vector2(0, 0), new Vector2(1, 0), new Vector2(0, 1), new Vector2(1, 1)], color)
        geometry.ref.addIndices([baseIndex + 0, baseIndex + 1, baseIndex + 2, baseIndex + 1, baseIndex + 3, baseIndex + 2])
        return Box2.fromMinMax(p00, p11)
    }

    private drawStrokePainter: HalPainterPrimitiveRef
    private drawStrokeGeometry: HalGeometryRef
    private strokeGeometryInfos: StrokeGeometryInfo[]
    private boundingBox: Box2
}

type StrokeGeometryInfo = {
    baseVertex: number
    baseIndex: number
    numVertices: number
    numIndices: number
    boundingBox: Box2
    brushStroke: BrushStroke
}
